import Vue from "vue";
import Vuex from "vuex";
import auth from "./modules/auth/auth";
import appointment from "./modules/getAppointment/appointment";
import addAppointment from "./modules/addAppointment/addAppointment";
import appointmentDetail from "./modules/appointmentDetail/appointmentDetail";
import search from "./modules/search/search";
import clients from "./modules/clients/clients";
import person from "./modules/person/person";
import event from "./modules/event/event";
import group from "./modules/group/group";
import addNew from "./modules/addNew/addNew";
import chat from "./modules/chat/chat";
import portalemail from "./modules/portalemail/portalemail";
import favourites from "./modules/favourites/favourites";
import selectedPhone from "./modules/selectedPhone/selectedPhone";
import forgotPassword from "./modules/forgotPassword/forgotPassword";
import { ERROR_MESSSAGE } from "./config"; // Base url for easy change between dev and prod environment
import { jsonStringify, jsonParse } from "../worker/index";
import axios from "axios";

/**
 * Refresh timer with every fetch
 * @param {Object} json
 * @param {Object} context
 */
function refreshLogoutTimer(json, context) {
  if ("timeRemain" in json) {
    const expiry = isNaN(json.timeRemain) ? 0 : parseInt(json.timeRemain);
    const timeRemain = expiry * 1000 - new Date().getTime();
    if (timeRemain >= 0) {
      context.dispatch("setTimeoutForLogout", timeRemain);
    } else {
      // Logout user
      if (context.getters.checkSession) {
        context.dispatch("logout");
      }
    }
  }
}

Vue.use(Vuex);
let globalMessageTimeout = false; // Global message timeout for hiding message once done

export const store = new Vuex.Store({
  state: {
    globalLoading: false,
    globalMessage: "",
    globalMessageAction: false,
    previousRoute: null,
    baseSecureVideoUrl: "", // The base secure video url
    initialLoadRoute: null
  },
  getters: {
    getGlobalMessage(state) {
      return state.globalMessage;
    },
    getGlobalMessageAction(state) {
      return state.globalMessageAction;
    },
    getGlobalLoading(state) {
      return state.globalLoading;
    },
    getPreviousRoute(state) {
      return state.previousRoute;
    },
    getBaseSecureVideoUrl(state) {
      return state.baseSecureVideoUrl;
    },
    getInitialLoadRoute(state) {
      return state.initialLoadRoute;
    }
  },
  mutations: {
    setGlobalMessage(state, value) {
      state.globalMessage = value;
    },
    setGlobalMessageAction(state, value) {
      state.globalMessageAction = value;
    },
    setGlobalLoading(state, value) {
      state.globalLoading = value;
    },
    setPreviousRoute(state, value) {
      state.previousRoute = value;
    },
    setBaseSecureVideoUrl(state, value) {
      state.baseSecureVideoUrl = value;
    },
    setInitialLoadRoute(state, value) {
      state.initialLoadRoute = value;
    }
  },
  actions: {
    /**
     * Get the base secure video url
     * @param {Object} context
     */
    async fetchSecureVideoBaseUrl(context) {
      // Step 1: Check if already fetched
      if (context.getters.getBaseSecureVideoUrl) {
        return;
      }

      // Step 2: Fetch if not already fetched
      const res = await axios("/secure_video/base_url");

      // Step 3: Set the data
      if (res.data && res.data.code && res.data.code === 200) {
        context.commit("setBaseSecureVideoUrl", res.data.base_url);
      }
    },
    /**
     * Empty the message and hence successfully hide the popup
     * @param {Object} context
     */
    clearGlobalMessage(context) {
      // Step 1: Clear time out, if it is there
      if (globalMessageTimeout) {
        clearTimeout(globalMessageTimeout);
        globalMessageTimeout = false;
      }

      // Step 2: Clear the message and action
      context.commit("setGlobalMessage", "");
      context.commit("setGlobalMessageAction", false);
    },
    /**
     * Set a global message and remove it after the specified time
     * @param {Object} state
     * @param {String} value
     */
    globalMessagePopup(context, value) {
      // Step 1: Set the timeout message and action
      let time = process.env.VUE_APP_GLOBAL_MESSAGE_EXPIRY_TIME;
      if (typeof value === "object") {
        context.commit("setGlobalMessage", value.message);
        context.commit("setGlobalMessageAction", value.action);
        if(value.time && value.time > 0) {
          time = value.time;
        }
      } else {
        context.commit("setGlobalMessage", value);
      }

      // Step 2: Clear the old timeout if there is any
      if (globalMessageTimeout) {
        clearTimeout(globalMessageTimeout);
      }

      // Step 3: Create another timeout and after the timeout remove the message
      globalMessageTimeout = setTimeout(() => {
        context.commit("setGlobalMessage", "");
        context.commit("setGlobalMessageAction", false);
        globalMessageTimeout = false;
      }, time * 1000);
    },
    /**
     * General request with auth tokens
     * @param {Object} context
     * @param {Object} data
     */
    makeAuthRequest(context, data) {
      return new Promise(async resolve => {
        let session = context.getters.userInfo;

        // Auth token required by the server
        session = {
          userId: session.userId,
          token: session.token
        };

        // Build request based on method type
        let requestParams = {
          url: "/" + data.url,
          method: data.method,
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: {
            "Content-Type": data.header=="multipart/form-data"?"multipart/form-data":"application/json; charset=utf-8",
            token: session.token,
            userid: session.userId
          },
          redirect: "follow",
          referrer: "no-referrer",
          validateStatus: status => {
            return status >= 200 && status < 300;
          },
          transformResponse: data => {
            return data;
          }
        };

        // Check if data needs to be stringified
        if (data.data) {
          if (data.stringify) {
            requestParams.data = data.data;
          } else {
            // Stringify data
            const workerRes = await jsonStringify(data.data);

            // Check for error
            if (workerRes.error) {
              return;
            }

            // Proceed it there is no error
            requestParams.data = workerRes.value;
          }

        }

        try {
          // Make query
          const res = await axios(requestParams);

          // Parse json
          const resJson = await jsonParse(res.data);
          if (resJson.error) {
            context.dispatch("globalMessagePopup", ERROR_MESSSAGE[500]);
            resolve({});
          }

          // Refresh logout timer
          refreshLogoutTimer(resJson.value, context);

          // Resolve
          resolve(resJson.value);
        } catch (err) {
          // Get the response
          const res = err.response;
          // Logout when user is unauthorized
          if (res != undefined) {
            if (res.status === 401) {
              context.dispatch("globalMessagePopup", ERROR_MESSSAGE[401]);
              if (context.getters.checkSession) {
                context.dispatch("logout");
              }
              return;
            }
            
            // Get the json
            const resJson = await jsonParse(res.data);
            
            // Return
            resolve(resJson.value);
          }
        }
      });
    },
    /**
     * General request with auth tokens
     * @param {Object} context
     * @param {Object} data
     */
    makeAuthRequestEmail(context, data) {
      return new Promise(async resolve => {
        let session = context.getters.userInfo;

        // Auth token required by the server
        session = {
          userId: session.userId,
          token: session.token
        };
        
        try {
          // Make query
          const res = await axios({
            url: "/" + data.url,
            method: data.method,
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
              "Content-Type": "multipart/form-data; charset=UTF-8",
              token: session.token,
              userid: session.userId
            },
            redirect: "follow",
            referrer: "no-referrer",
            data: data.data
            });

          // Parse json
          /*const resJson = await jsonParse(res.data);
          if (resJson.error) {
            context.dispatch("globalMessagePopup", ERROR_MESSSAGE[500]);
            resolve({});
          }*/

          // Refresh logout timer
          // refreshLogoutTimer(resJson.value, context);

          // Resolve
          resolve(res.data);
        } catch (err) {
          // Get the response
          const res = err.response;
          // Logout when user is unauthorized
          if (res != undefined) {
            if (res.status === 401) {
              context.dispatch("globalMessagePopup", ERROR_MESSSAGE[401]);
              if (context.getters.checkSession) {
                context.dispatch("logout");
              }
              return;
            }
            
            // Get the json
            // const resJson = await jsonParse(res.data);
            
            // Return
            // resolve(resJson.value);
            resolve(res.data);
          }
        }
      });
    }
  },
  modules: {
    auth,
    appointment,
    addAppointment,
    appointmentDetail,
    search,
    clients,
    addNew,
    person,
    event,
    group,
    chat,
    portalemail,
    favourites,
    selectedPhone,
    forgotPassword
  }
});
